/**
 * Company: SPHMedia
 * Description: This is the base page component for all BH pages
 */

import CASBlock from "@components/Blocks/CASBlock/CASBlock";
import AdsContainer from "@components/Blocks/Container/AdsContainer";
import { MainContainerNew } from "@components/Blocks/Container/MainContainerNew";
import CatfishAd from "@components/Elements/Advertisement/CatfishAd";
import { GoogleAdsSlotFactory } from "@components/Elements/Advertisement/helpers";
import PrestitialAd from "@components/Elements/Advertisement/PrestitialAd";
import { FooterNew } from "@components/Modules/FooterNew/FooterNew";
import { HeaderNew } from "@components/Modules/HeaderNew/HeaderNew";
import { pubPageAdTargeting } from "@config/ads";
import { AdsLb1 } from "@elements/Advertisement/variant/AdsLb";
import type { GADataProps } from "@elements/GAData/GAData";
import GAData from "@elements/GAData/GAData";
import type { MetaTagsProps } from "@elements/MetaTags/MetaTags";
import MetaTags from "@elements/MetaTags/MetaTags";
import { CAS_EI_MODEL_ID, IS_PLAYWRIGHT_TEST } from "@helper/getEnvVariables";
import { useNeuronPageViewPush } from "@hooks/useAnalyticsCall/useNeuronPageViewPush";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { useHideInvisibleAds } from "@hooks/useHideInvisibleAds";
import useSendPermutiveIdentify from "@hooks/useSendPermutiveIdentify";
import {
  AdTargetingType,
  ArticleAdTargetKeys,
  PageAdTargetValue,
} from "@typings/Ads.d";
import { ReactElement } from "react";
import { useLocation } from "react-router";
import { twMerge } from "tailwind-merge";

export type BHBasePageProps = {
  uniqueSectionName?: string;
  adsSectionName?: string;
  children?: React.ReactNode;
  hasLb1Ads?: boolean;
  gaData?: GADataProps;
  metaTags?: MetaTagsProps;
  pageAdTargetValue: PageAdTargetValue;
  hideScrollableSectionMenu?: boolean;
  adsSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  bodyWrapperStyle?: string;
  showGEBanner?: boolean;
};

/**
 * BHBasePage
 * Description: The base page component for all BH pages
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function BHBasePage({
  uniqueSectionName,
  adsSectionName = uniqueSectionName,
  children,
  hasLb1Ads = true,
  gaData,
  metaTags,
  pageAdTargetValue,
  hideScrollableSectionMenu = false,
  adsSlotTarget,
  bodyWrapperStyle = "",
  showGEBanner = true,
}: BHBasePageProps): ReactElement {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isMobWebView = searchParams.get("mobwebview");
  const {
    isPrestitialEnabled,
    isCatfishEnabled,
    topOverlayImpressions,
    topOverlayValidity,
  } = useGlobalAdSetting();
  useNeuronPageViewPush();
  useSendPermutiveIdentify();
  useHideInvisibleAds();

  const pageAdTarget = pubPageAdTargeting[pageAdTargetValue] as AdTargetingType<
    string,
    PageAdTargetValue
  >;

  const renderPage = () => {
    return (
      <>
        <MainContainerNew>
          <AdsContainer className="flex justify-center border-b-[1px] border-black-900">
            {hasLb1Ads && !isMobWebView && (
              <AdsLb1
                uniqueSectionName={adsSectionName}
                pageAdTargetType={pageAdTargetValue}
                hasStripeBg={false}
                wrapperStyle="mb-0"
                articleSlotTarget={adsSlotTarget}
              />
            )}
          </AdsContainer>
          {!isMobWebView && (
            <HeaderNew
              isHomePage={uniqueSectionName === "home" ? true : false}
              hideScrollableSectionMenu={hideScrollableSectionMenu}
              showGEBanner={showGEBanner}
            />
          )}
          <div className={twMerge("my-3 lg:my-md w-full", bodyWrapperStyle)}>
            {IS_PLAYWRIGHT_TEST !== "true" && (
              <PrestitialAd
                slot={GoogleAdsSlotFactory.prestitial(adsSectionName)}
                isPrestitialEnabled={isPrestitialEnabled}
                pubAdTargeting={[pageAdTarget]}
                topOverlayImpressions={topOverlayImpressions}
                topOverlayValidity={topOverlayValidity}
                articleSlotTarget={adsSlotTarget}
              />
            )}
            {children}
          </div>
        </MainContainerNew>
        {!isMobWebView && <FooterNew />}
      </>
    );
  };

  return (
    <>
      {gaData && <GAData {...gaData} />}
      {metaTags && <MetaTags {...metaTags} />}
      {renderPage()}
      {isCatfishEnabled && (
        <CatfishAd
          slot={GoogleAdsSlotFactory.catfish(adsSectionName)}
          isCatfishEnabled={isCatfishEnabled}
          pubAdTargeting={[pageAdTarget]}
          articleSlotTarget={adsSlotTarget}
        />
      )}
      <CASBlock casId={CAS_EI_MODEL_ID} />
    </>
  );
}
